import { createTask, type Task, type TaskExtrasEntry } from "@app/core";
import { iso } from "./task-graph.js";
import type { ReconcileDeps } from "./types.js";

/**
 * Apply the user's BRACKET edits from a task event to the local subtree. Each
 * bracket line is the sole user signal we consume:
 *  - title matches a KNOWN subtask (from `tcState`) → toggle that local child BY ID
 *    (tombstone-aware: a deleted/missing id is skipped, never resurrected);
 *  - a NEW title → create a direct child (unless a live descendant already has it —
 *    a light dup guard that also covers legacy events with no `tcState`).
 * Emoji/display lines are never seen here (the parser drops them). Line removals in
 * Google are ignored (structural deletes from a fragile surface aren't trusted).
 * Returns whether anything changed.
 */
export async function reconcileSubtaskEdits(
  deps: ReconcileDeps,
  parent: Task,
  entry: TaskExtrasEntry,
  now: Date,
  // The full task list (incl. soft-deleted), read ONCE by the caller and kept
  // current HERE (updates replaced, creates pushed) so a later entry's dup
  // guard sees this entry's writes without a listAll() per entry.
  allTasks: Task[],
): Promise<boolean> {
  if (!entry.bracketEdits.length) return false;
  const norm = (s: string) => s.trim().toLowerCase();
  const taskById = new Map(allTasks.map((c) => [c.id, c] as const));

  // Live descendant titles (dup guard) + the next sibling order under `parent`.
  const childListOf = new Map<string, Task[]>();
  for (const t of allTasks) {
    if (!t.subtask_parent_id || t.deleted_at) continue;
    const list = childListOf.get(t.subtask_parent_id) ?? [];
    list.push(t);
    childListOf.set(t.subtask_parent_id, list);
  }
  const liveTitles = new Set<string>();
  const gather = (id: string) => {
    for (const c of childListOf.get(id) ?? []) {
      liveTitles.add(norm(c.title));
      gather(c.id);
    }
  };
  gather(parent.id);

  const knownByTitle = new Map(entry.knownSubtasks.map((r) => [norm(r.title), r] as const));
  let order = (childListOf.get(parent.id) ?? []).length;
  let changed = false;

  for (const edit of entry.bracketEdits) {
    const ref = knownByTitle.get(norm(edit.title));
    if (ref) {
      const local = taskById.get(ref.id);
      if (!local || local.deleted_at) continue; // tombstone-aware — never resurrect
      const want: Task["status"] = edit.done ? "done" : "open";
      if (local.status !== want) {
        const updated: Task = {
          ...local,
          status: want,
          completed_at: edit.done ? iso(now) : null,
          updated_at: iso(now),
        };
        // The toggle came FROM Google's bracket line — google-acked by
        // construction, server-dirty so peers learn it.
        await deps.tasks.update(updated, "google");
        taskById.set(updated.id, updated);
        const idx = allTasks.indexOf(local);
        if (idx >= 0) allTasks[idx] = updated;
        changed = true;
      }
    } else if (!liveTitles.has(norm(edit.title))) {
      const base = createTask(
        {
          title: edit.title,
          calendar_id: parent.calendar_id,
          subtask_parent_id: parent.id,
          subtask_order: order++,
        },
        () => now,
      );
      const inserted = edit.done ? { ...base, status: "done" as const, completed_at: iso(now) } : base;
      await deps.tasks.insert(inserted, "google");
      allTasks.push(inserted);
      liveTitles.add(norm(edit.title));
      changed = true;
    }
  }
  return changed;
}
